import React, { useState, useEffect } from 'react';
import MeetingsAPI, { ReviewablePendingBookDto } from '../../api/MeetingsAPI';
import { useTranslation } from 'react-i18next';
import Tab from '../Tab/Tab';
import PendingMeetingCard from './PendingMeetingCard';
import PendingRefundingCard from './PendingRefundingCard';
import RejectedMeetingCard from './RejectedMeetingCard';
import { toastService } from '../../services/ToastService';

type PendingListTab = 'pending' | 'refunds' | 'rejected';

const BookingMeetingList: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [pendingMeetings, setPendingMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [refundableMeetings, setRefundableMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [rejectedMeetings, setRejectedMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [activeTab, setActiveTab] = useState<PendingListTab>('pending');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchMeetings = async () => {
    setIsLoading(true);
    try {
      const response = await MeetingsAPI.pendings(activeTab);

      if (activeTab === 'refunds') {
        setRefundableMeetings(response.data.meetings);
      } else if (activeTab === 'rejected') {
        setRejectedMeetings(response.data.meetings);
      } else {
        setPendingMeetings(response.data.meetings);
      }
    } catch (error) {
      console.error('Failed to fetch meetings', { activeTab, error });
    } finally {
      setIsLoading(false);
    }
  };

  const dispatchPendingMeetingsUpdated = () => {
    window.dispatchEvent(new CustomEvent('pendingMeetingsUpdated'));
  };

  const handleBookingAction = async (id: number, action: 'accept' | 'reject') => {
    try {
      await MeetingsAPI[action](id);

      const updatedMeetings = pendingMeetings.filter((meeting) => meeting.id !== id);
      setPendingMeetings(updatedMeetings);

      if (updatedMeetings.length === 0 && onClose) {
        onClose();
      }

      dispatchPendingMeetingsUpdated();
    } catch (error) {
      console.error(`Error ${action}ing booking`, { id, error });
    }
  };

  const handleReaccept = async (id: number) => {
    try {
      await MeetingsAPI.accept(id);

      const updatedMeetings = rejectedMeetings.filter((meeting) => meeting.id !== id);
      setRejectedMeetings(updatedMeetings);

      toastService.showToast(
        t('booking.pending.reaccept_success') || 'Booking accepted',
        'success'
      );
      dispatchPendingMeetingsUpdated();
    } catch (error) {
      console.error('Error re-accepting rejected booking', { id, error });
      toastService.showToast(
        t('booking.pending.reaccept_error') || 'Failed to accept booking',
        'error'
      );
      throw error;
    }
  };

  const handleRefundProcessed = async (id: number) => {
    const updatedMeetings = refundableMeetings.filter((meeting) => meeting.id !== id);
    setRefundableMeetings(updatedMeetings);

    if (updatedMeetings.length === 0 && onClose) {
      onClose();
    }

    dispatchPendingMeetingsUpdated();
  };

  const handlePaymentStatusChange = async (id: number, paid: boolean) => {
    try {
      await MeetingsAPI.updatePaymentStatus(id, paid);

      const updateList = (meetings: ReviewablePendingBookDto[]) =>
        meetings.map((meeting) => (meeting.id === id ? { ...meeting, paid } : meeting));

      setPendingMeetings(updateList);
      setRejectedMeetings(updateList);
    } catch (error) {
      console.error('Error updating payment status', { id, paid, error });
      toastService.showToast(
        t('booking.pending.payment_update_error') || 'Failed to update payment status',
        'error'
      );
    }
  };

  return (
    <div className="p-1 space-y-5">
      <div className="flex border-b border-gray-200">
        <Tab
          label={t('booking.pending.tab_pending') || 'Pending'}
          isActive={activeTab === 'pending'}
          fn={() => setActiveTab('pending')}
        />
        <Tab
          label={t('booking.pending.tab_rejected') || 'Rejected'}
          isActive={activeTab === 'rejected'}
          fn={() => setActiveTab('rejected')}
        />
        <Tab
          label={t('booking.pending.tab_refunds') || 'Refunds'}
          isActive={activeTab === 'refunds'}
          fn={() => setActiveTab('refunds')}
        />
      </div>

      <div className="mt-4 space-y-4">
        {activeTab === 'pending' &&
          (isLoading ? (
            <div className="text-center py-4">{t('global.loading') || 'Loading...'}</div>
          ) : pendingMeetings.length === 0 ? (
            <div className="text-center py-4">
              {t('booking.pending.no_pending') || 'No pending bookings'}
            </div>
          ) : (
            pendingMeetings.map((meeting) => (
              <PendingMeetingCard
                key={meeting.id}
                meeting={meeting}
                onAction={handleBookingAction}
                onPaymentStatusChange={handlePaymentStatusChange}
              />
            ))
          ))}

        {activeTab === 'rejected' &&
          (isLoading ? (
            <div className="text-center py-4">{t('global.loading') || 'Loading...'}</div>
          ) : rejectedMeetings.length === 0 ? (
            <div className="text-center py-4">
              {t('booking.pending.no_rejected') || 'No recently rejected bookings'}
            </div>
          ) : (
            rejectedMeetings.map((meeting) => (
              <RejectedMeetingCard
                key={meeting.id}
                meeting={meeting}
                onAccept={handleReaccept}
                onPaymentStatusChange={handlePaymentStatusChange}
              />
            ))
          ))}

        {activeTab === 'refunds' &&
          (isLoading ? (
            <div className="text-center py-4">{t('global.loading') || 'Loading...'}</div>
          ) : refundableMeetings.length === 0 ? (
            <div className="text-center py-4">
              {t('booking.pending.no_refunds') || 'No pending refunds'}
            </div>
          ) : (
            refundableMeetings.map((meeting) => (
              <PendingRefundingCard
                key={meeting.id}
                meeting={meeting}
                onRefundProcessed={handleRefundProcessed}
              />
            ))
          ))}
      </div>
    </div>
  );
};

export default BookingMeetingList;
